/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { useMPSBusinessProfile } from "../../../context/MPSBusinessProfileContext";
import { validatePhoneNumber, validateEmail } from "../../../utils/validations";

const businessTypeOptions = [
  "Artisan / Crafted Goods",
  "Beverage – Non-Alcoholic",
  "Beverage – Craft Alcohol",
  "Children's Goods",
  "Clothing & Apparel",
  "Educational / Services",
  "Food – Prepared",
  "Food – Service",
  "Food Truck",
  "Health, Wellness & Beauty",
  "Meats & Produce",
  "Flowers & Plants",
  "Jewelry",
];

const BusinessInfoFormPage1 = ({ handleContinue }) => {
  const { user } = useAuth();
  const { MPSBusinessProfileData, updateMPSBusinessProfile } = useMPSBusinessProfile();

  const [formState, setFormState] = useState({
    businessName: MPSBusinessProfileData.businessName || "",
    fullName: MPSBusinessProfileData.fullName || "",
    businessEmail: MPSBusinessProfileData.businessEmail || user?.email || "",
    businessPhoneNumber: MPSBusinessProfileData.businessPhoneNumber || "",
    businessType: MPSBusinessProfileData.businessType || "",
    businessCity: MPSBusinessProfileData.businessCity || "",
  });
  const [errors, setErrors] = useState({});

  const handleFormChange = (field, value) => {
    setFormState(prev => ({ ...prev, [field]: value }));
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    handleFormChange("businessEmail", value);
    if (value && !validateEmail(value)) {
      setErrors(prev => ({ ...prev, businessEmail: "Please enter a valid email" }));
    } else {
      setErrors(prev => { const n = { ...prev }; delete n.businessEmail; return n; });
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    handleFormChange("businessPhoneNumber", value);
    if (value && !validatePhoneNumber(value)) {
      setErrors(prev => ({ ...prev, businessPhoneNumber: "Please enter a valid phone number" }));
    } else {
      setErrors(prev => { const n = { ...prev }; delete n.businessPhoneNumber; return n; });
    }
  };

  const canContinue =
    formState.businessName.trim() &&
    formState.businessType &&
    formState.businessEmail &&
    formState.businessPhoneNumber &&
    Object.keys(errors).length === 0;

  const handleSubmit = () => {
    if (!canContinue) return;
    updateMPSBusinessProfile({
      userId: user.uid,
      businessName: formState.businessName.trim(),
      fullName: formState.fullName.trim(),
      businessEmail: formState.businessEmail,
      businessPhoneNumber: formState.businessPhoneNumber,
      businessType: formState.businessType,
      businessCity: formState.businessCity.trim(),
    });
    handleContinue();
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-semibold text-grey-900 mb-2">Tell us about your business</h1>
        <p className="text-grey-500 text-sm">This helps event organizers find and approve you faster.</p>
      </div>

      <div className="space-y-5">
        {/* Business Name */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-grey-900">
            Business Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formState.businessName}
            onChange={e => handleFormChange("businessName", e.target.value)}
            placeholder="e.g. Sunrise Candle Co."
            className="w-full px-4 py-3 border border-grey-300 rounded-xl text-grey-900 placeholder-grey-400 focus:ring-2 focus:ring-grey-900 focus:border-transparent transition"
          />
        </div>

        {/* Business Type — pill grid */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-grey-900">
            Business Type <span className="text-red-500">*</span>
          </label>
          <div className="flex flex-wrap gap-2">
            {businessTypeOptions.map(type => (
              <button
                key={type}
                type="button"
                onClick={() => handleFormChange("businessType", type)}
                className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-all ${
                  formState.businessType === type
                    ? "bg-grey-900 text-white border-grey-900"
                    : "bg-white text-grey-700 border-grey-300 hover:border-grey-500"
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        {/* City */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-grey-900">
            Your City
            <span className="ml-2 text-xs font-normal text-grey-400">Used to recommend nearby events</span>
          </label>
          <input
            type="text"
            value={formState.businessCity}
            onChange={e => handleFormChange("businessCity", e.target.value)}
            placeholder="e.g. Portland, OR"
            className="w-full px-4 py-3 border border-grey-300 rounded-xl text-grey-900 placeholder-grey-400 focus:ring-2 focus:ring-grey-900 focus:border-transparent transition"
          />
        </div>

        {/* Contact + Email */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-grey-900">Contact Name</label>
            <input
              type="text"
              value={formState.fullName}
              onChange={e => handleFormChange("fullName", e.target.value)}
              placeholder="Your full name"
              className="w-full px-4 py-3 border border-grey-300 rounded-xl text-grey-900 placeholder-grey-400 focus:ring-2 focus:ring-grey-900 focus:border-transparent transition"
            />
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-grey-900">
              Business Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formState.businessEmail}
              onChange={handleEmailChange}
              placeholder="hello@yourbusiness.com"
              className={`w-full px-4 py-3 border rounded-xl text-grey-900 placeholder-grey-400 focus:ring-2 focus:ring-grey-900 focus:border-transparent transition ${
                errors.businessEmail ? "border-red-400" : "border-grey-300"
              }`}
            />
            {errors.businessEmail && (
              <p className="text-xs text-red-500">{errors.businessEmail}</p>
            )}
          </div>
        </div>

        {/* Phone */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-grey-900">
            Phone Number <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            value={formState.businessPhoneNumber}
            onChange={handlePhoneChange}
            placeholder="+1 (555) 000-0000"
            className={`w-full px-4 py-3 border rounded-xl text-grey-900 placeholder-grey-400 focus:ring-2 focus:ring-grey-900 focus:border-transparent transition ${
              errors.businessPhoneNumber ? "border-red-400" : "border-grey-300"
            }`}
          />
          {errors.businessPhoneNumber && (
            <p className="text-xs text-red-500">{errors.businessPhoneNumber}</p>
          )}
        </div>

        {/* Continue */}
        <button
          onClick={handleSubmit}
          disabled={!canContinue}
          className={`w-full py-3.5 rounded-xl font-semibold text-sm transition-all mt-4 ${
            canContinue
              ? "bg-grey-900 text-white hover:bg-grey-800"
              : "bg-grey-200 text-grey-400 cursor-not-allowed"
          }`}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default BusinessInfoFormPage1;
